import { type ReactNode, useEffect, useRef, useState } from 'react'

/**
 * TK redesign v2: a number that counts up to its value.
 *
 * Takes the already-formatted text ("1,098", "98.3%", "1.63 x 10^8" in CJK units, "564ms") and
 * animates only the numeric part, keeping the prefix, suffix, separators and
 * decimals exactly as the formatter wrote them. Anything that is not a single
 * number is rendered untouched. Reduced motion renders the final value at once.
 */

const NUMBER_RE = /^(\D*?)(-?\d{1,3}(?:,\d{3})+(?:\.\d+)?|-?\d+(?:\.\d+)?)(.*)$/s
const DURATION_MS = 520

interface Parsed {
  prefix: string
  value: number
  suffix: string
  decimals: number
  grouped: boolean
}

function parse(text: string): Parsed | null {
  const match = NUMBER_RE.exec(text)
  if (!match) return null
  const [, prefix = '', digits = '', suffix = ''] = match
  const value = Number(digits.replace(/,/g, ''))
  if (!Number.isFinite(value)) return null
  const dot = digits.indexOf('.')
  return {
    prefix,
    value,
    suffix,
    decimals: dot === -1 ? 0 : digits.length - dot - 1,
    grouped: digits.includes(','),
  }
}

function render(parsed: Parsed, value: number): string {
  const fixed = value.toFixed(parsed.decimals)
  if (!parsed.grouped) return `${parsed.prefix}${fixed}${parsed.suffix}`
  const [whole = '', fraction] = fixed.split('.')
  const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${parsed.prefix}${groupedWhole}${fraction !== undefined ? `.${fraction}` : ''}${parsed.suffix}`
}

function reducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

export interface CountUpProps {
  value: ReactNode
}

export function CountUp({ value }: CountUpProps) {
  const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value : null
  const parsed = text === null ? null : parse(text)
  const [shown, setShown] = useState<string | null>(null)
  const from = useRef<number | null>(null)

  useEffect(() => {
    if (!parsed || reducedMotion()) return undefined
    const target = parsed.value
    const origin = from.current
    from.current = target
    // First paint shows the real figure. Only a later change interpolates,
    // and never from zero, so a refresh does not invent a climb.
    if (origin === null || origin === target) return undefined
    const start = performance.now()
    let frame = 0
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / DURATION_MS)
      const eased = 1 - Math.pow(1 - progress, 3)
      setShown(render(parsed, origin + (target - origin) * eased))
      if (progress < 1) frame = requestAnimationFrame(tick)
      else from.current = target
    }
    frame = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(frame)
      from.current = target
    }
    // parsed is derived from text; text is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text])

  if (!parsed) return <>{value}</>
  return <>{shown ?? render(parsed, parsed.value)}</>
}
